import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LogIn } from 'lucide-react';
import { supabase } from '../../../utils/supabase';
import { useLoginForm } from '../../../hooks/useLoginForm';
import { ErrorMessage } from './ErrorMessage';
import { RememberMe } from './RememberMe';
import { LoginLinks } from './LoginLinks';
import { LoginRecaptcha } from './LoginRecaptcha';
import { RECAPTCHA_SITE_KEY } from '../../../utils/constants';

export function LoginForm() {
  const [isInitializing, setIsInitializing] = useState(true);
  const [recaptchaToken, setRecaptchaToken] = useState<string|null>(null);
  const [showRecaptcha, setShowRecaptcha] = useState(false);

  const {
    email,
    password,
    rememberMe,
    error,
    isLoading,
    handleEmailChange,
    handlePasswordChange,
    handleRememberMeChange,
    handleSubmit: baseHandleSubmit
  } = useLoginForm();

  // Show reCAPTCHA after 3 failed attempts
  const showingRecaptcha = (email:string)=>{
    const attempts = parseInt(localStorage.getItem(`login-attempts-${email}`) || '0', 10);
    setShowRecaptcha(attempts >= 5);
  }


  useEffect(() => {
    showingRecaptcha(email);
  }, [email]);

  // Wrap handleSubmit to require recaptcha if needed
  const handleSubmit = (e: React.FormEvent) => {
    showingRecaptcha(email);

    if (showRecaptcha && !recaptchaToken) {
      e.preventDefault();
      alert('reCAPTCHAを完了してください');
      return;
    }
    baseHandleSubmit(e, recaptchaToken);
  };

  // Check if there's a session already on component mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          console.log('Active session detected on login page');
        }
      } catch (err) {
        console.error('Error checking session:', err);
      } finally {
        setIsInitializing(false);
      }
    };
    
    checkSession();
  }, []);
  
  if (isInitializing) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return (
    <div className="mt-8 space-y-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <div className="rounded-md shadow-sm">
            <label htmlFor="email-address" className="sr-only">
              メールアドレス
            </label>
            <input
              id="email-address"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={handleEmailChange}
              className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm cursor-text"
              placeholder="メールアドレス"
            />
          </div>
          <div className="rounded-md shadow-sm">
            <label htmlFor="password" className="sr-only">
              パスワード
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={handlePasswordChange}
              className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm cursor-text"
              placeholder="パスワード"
            />
          </div>
        </div>

        <ErrorMessage error={error} />

        <div className="flex items-center justify-between">
          <RememberMe checked={rememberMe} onChange={handleRememberMeChange} />
          <LoginLinks />
        </div>

        {showRecaptcha && (
          <LoginRecaptcha siteKey={RECAPTCHA_SITE_KEY} onChange={setRecaptchaToken} />
        )}

        <div>
          <button
            type="submit"
            disabled={isLoading || (showRecaptcha && !recaptchaToken)}
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
          >
            <span className="absolute left-0 inset-y-0 flex items-center pl-3">
              <LogIn className={`h-5 w-5 text-blue-500 group-hover:text-blue-400 ${
                isLoading ? 'animate-pulse' : ''
              }`} />
            </span>
            {isLoading ? 'ログイン中...' : 'ログイン'}
          </button>
        </div>
      </form>

      <div className="mt-6 text-center text-sm text-gray-600">
        アカウントをお持ちでない方は{' '} 
        <Link to="/signup" className="font-medium text-blue-600 hover:text-blue-500">
          新規会員登録
        </Link>
      </div>
    </div>
  );
}